<script setup lang="ts">
  import '@wangeditor/editor/dist/css/style.css'
  import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
  import type { IDomEditor, IEditorConfig, IToolbarConfig } from '@wangeditor/editor'
  import { useMessage } from 'naive-ui'

  const model = defineModel<string>({ default: '' })

  const props = withDefaults(
    defineProps<{
      placeholder?: string
      disabled?: boolean
      height?: number
    }>(),
    {
      placeholder: '请输入正文内容…',
      disabled: false,
      height: 420,
    },
  )

  const message = useMessage()
  const editorRef = shallowRef<IDomEditor | null>(null)
  const mode = 'default'

  const toolbarConfig: Partial<IToolbarConfig> = {
    excludeKeys: ['fullScreen'],
  }

  const editorConfig = computed<Partial<IEditorConfig>>(() => ({
    placeholder: props.placeholder,
    readOnly: props.disabled,
    MENU_CONF: {
      uploadImage: {
        maxFileSize: 5 * 1024 * 1024,
        allowedFileTypes: ['image/*'],
        async customUpload(file: File, insertFn: (url: string, alt: string, href: string) => void) {
          const formData = new FormData()
          formData.append('file', file)
          try {
            const res = await $fetch<{ ok: boolean; url: string; name: string }>('/api/admin/upload/image', {
              method: 'POST',
              body: formData,
            })
            insertFn(res.url, res.name || file.name, res.url)
          } catch (err: unknown) {
            const statusMessage =
              err && typeof err === 'object' && 'data' in err
                ? String((err as { data?: { statusMessage?: string } }).data?.statusMessage || '')
                : ''
            message.error(statusMessage || '图片上传失败')
          }
        },
      },
    },
  }))

  function handleCreated(editor: IDomEditor) {
    editorRef.value = editor
  }

  watch(
    () => props.disabled,
    (disabled) => {
      const editor = editorRef.value
      if (!editor) return
      if (disabled) editor.disable()
      else editor.enable()
    },
  )

  onBeforeUnmount(() => {
    const editor = editorRef.value
    if (!editor) return
    editor.destroy()
    editorRef.value = null
  })
</script>

<template>
  <div class="rich-editor" :class="{ 'rich-editor--disabled': disabled }">
    <Toolbar
      class="rich-editor__toolbar"
      :editor="editorRef"
      :default-config="toolbarConfig"
      :mode="mode"
    />
    <Editor
      v-model="model"
      class="rich-editor__body"
      :style="{ height: `${height}px` }"
      :default-config="editorConfig"
      :mode="mode"
      @on-created="handleCreated"
    />
  </div>
</template>

<style lang="scss" scoped>
  .rich-editor {
    width: 100%;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
  }

  .rich-editor__toolbar {
    border-bottom: 1px solid #eef0f3;
  }

  .rich-editor__body {
    overflow-y: hidden;
  }

  .rich-editor--disabled {
    opacity: 0.7;
    pointer-events: none;
  }

  .rich-editor :deep(.w-e-text-container) {
    background: #fff;
  }

  .rich-editor :deep(.w-e-text-placeholder) {
    color: #9ca3af;
  }
</style>
